/* eslint-disable max-statements */
/* eslint-disable complexity */
import { useContext, useMemo } from 'react';
import { ControlledDateTimePickerContext } from '../../ControlledDateTimePickerCTX.js';
import { deconstructValuesFromDateString } from '../stringHelpers.js';
import { convertToPositiveNumberIfPossible } from '../numberHelpers.js';
import { getDateValuesFromDate, isValidDayForTheMonth } from '../dateHelpers.js';
import { useGetDayFromDateString } from './useGetDayFromDateString.js';
import type { DSControlledDateTimePickerInternalsT } from '../../sharedTypes.js';

interface StartingFocusedDayT {
  startFocusedYear: number | null;
  startFocusedDay: number | null;
  startFocusedMonth: number | null;
  startFocusedDate: Date | null;
}
const empty = {
  startFocusedYear: null,
  startFocusedDay: null,
  startFocusedMonth: null,
  startFocusedDate: null,
};
export const useGetStartingFocusedDay = () => {
  const { onCalendarOpenFocusedDay } = useContext(ControlledDateTimePickerContext);
  const dayFromInputValues = useGetDayFromDateString();
  const {
    month: inputValuesSplitMonth,
    day: inputValuesSplitDay,
    year: inputValuesDaySplitYear,
  } = dayFromInputValues ? getDateValuesFromDate(dayFromInputValues) : { month: null, day: null, year: null };
  // since month is 0 based we compare it with null
  const isInputFilledAndValid =
    inputValuesSplitMonth !== null &&
    inputValuesSplitDay !== null &&
    inputValuesDaySplitYear !== null &&
    isValidDayForTheMonth(
      inputValuesSplitMonth,
      inputValuesDaySplitYear,
      inputValuesSplitDay as DSControlledDateTimePickerInternalsT.DateDay,
    );
  const isOnCalendarOpenFocusedDayEmpty = !onCalendarOpenFocusedDay;
  const shouldFillWithOnCalendarOpenFocusedDay = !isInputFilledAndValid && !isOnCalendarOpenFocusedDayEmpty;
  return useMemo(() => {
    const startingDay: StartingFocusedDayT = { ...empty };

    // if input was filled start focus is the filled value (if it's valid day for the month)
    if (isInputFilledAndValid) {
      startingDay.startFocusedMonth = inputValuesSplitMonth;
      startingDay.startFocusedDay = inputValuesSplitDay!;
      startingDay.startFocusedYear = inputValuesDaySplitYear;
      startingDay.startFocusedDate = dayFromInputValues;
      // if input was not filled (or filled invalid) and there is no onCalendarOpenFocusedDay start focus is not a day
    } else if (isOnCalendarOpenFocusedDayEmpty) {
      // if input was not filled and there is onCalendarOpenFocusedDay start focus is onCalendarOpenFocusedDay day
      return empty;
    } else if (shouldFillWithOnCalendarOpenFocusedDay) {
      const {
        month: focusedDaySplitMonth,
        day: focusedDaySplitDay,
        year: focusedDaySplitYear,
      } = deconstructValuesFromDateString(onCalendarOpenFocusedDay);
      const monthAsInt = convertToPositiveNumberIfPossible(focusedDaySplitMonth);
      const dayAsInt = convertToPositiveNumberIfPossible(focusedDaySplitDay);
      const yearAsInt = convertToPositiveNumberIfPossible(focusedDaySplitYear);
      // if this condition is false, return will be the same as return empty
      // because startFocusedMonth/Day/Year/Date will be === null
      // which is the expected result
      if (
        monthAsInt !== -1 &&
        dayAsInt !== -1 &&
        yearAsInt !== -1 &&
        isValidDayForTheMonth(
          (monthAsInt - 1) as DSControlledDateTimePickerInternalsT.DateMonth,
          yearAsInt,
          dayAsInt as DSControlledDateTimePickerInternalsT.DateDay,
        )
      ) {
        startingDay.startFocusedMonth = monthAsInt;
        startingDay.startFocusedDay = dayAsInt;
        startingDay.startFocusedYear = yearAsInt;
        startingDay.startFocusedDate = new Date(yearAsInt, monthAsInt - 1, dayAsInt);
      }
    }
    return startingDay;
  }, [
    dayFromInputValues,
    inputValuesDaySplitYear,
    inputValuesSplitDay,
    inputValuesSplitMonth,
    isInputFilledAndValid,
    isOnCalendarOpenFocusedDayEmpty,
    onCalendarOpenFocusedDay,
    shouldFillWithOnCalendarOpenFocusedDay,
  ]);
};
